/*
  President Jewellers — Dynamic delivery date renderer

  Scans all text nodes for the marker pattern  [PJ_DELIVERY:MIN-MAX]
  and replaces it with a working-day date range, e.g. "Tue 17 Jun – Wed 24 Jun".

  Why a plain text marker rather than data-attributes on a span?
  Shopify's richtext / text setting sanitiser strips custom data-* attributes
  on theme push. A literal text marker survives JSON template validation and
  works inside richtext, plain text, and HTML settings alike.

  Usage in any Liquid / HTML / JSON template setting:
    Estimated delivery: [PJ_DELIVERY:7-14]
    Loose diamonds arrive in [PJ_DELIVERY:5-7].

  Working-day rules:
    - Skips Saturday + Sunday
    - Does NOT account for UK bank holidays (v1)
    - Cutoff: 2pm local time
        - Before 2pm AND today is a working day: today counts as day 1
        - At or after 2pm, OR today is a weekend: count starts from the next working day
*/

var PJ_DELIVERY_CUTOFF_HOUR = 14;
var PJ_DELIVERY_MARKER = /\[PJ_DELIVERY:(\d+)-(\d+)\]/g;

(function () {
  'use strict';

  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function isWeekend(d) {
    var dow = d.getDay();
    return dow === 0 || dow === 6;
  }

  // Returns the Nth working day from now, honouring the 2pm cutoff:
  //   - If today is a working day AND current time is before cutoff, today counts as day 1.
  //   - Otherwise, counting begins on the next working day (today is skipped).
  function workingDayFromNow(now, n) {
    var d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var todayCounts = !isWeekend(d) && now.getHours() < PJ_DELIVERY_CUTOFF_HOUR;
    var remaining = todayCounts ? n - 1 : n;
    while (remaining > 0) {
      d.setDate(d.getDate() + 1);
      if (!isWeekend(d)) remaining--;
    }
    return d;
  }

  function formatDate(d) {
    return DAYS_SHORT[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS_SHORT[d.getMonth()];
  }

  function hydrate(root) {
    var scope = (root && root.nodeType ? root : document.body) || document.documentElement;
    if (!scope) return;

    // Walk every text node under scope and check for the marker.
    var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null);
    var targets = [];
    var n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue && n.nodeValue.indexOf('[PJ_DELIVERY:') !== -1) {
        targets.push(n);
      }
    }
    if (!targets.length) return;

    var now = new Date();
    targets.forEach(function (textNode) {
      var original = textNode.nodeValue;
      PJ_DELIVERY_MARKER.lastIndex = 0;
      var replaced = original.replace(PJ_DELIVERY_MARKER, function (match, minStr, maxStr) {
        var min = parseInt(minStr, 10);
        var max = parseInt(maxStr, 10);
        if (isNaN(min) || isNaN(max) || min < 1 || max < min) return match;
        var earliest = workingDayFromNow(now, min);
        var latest = workingDayFromNow(now, max);
        return formatDate(earliest) + ' – ' + formatDate(latest);
      });
      if (replaced !== original) {
        textNode.nodeValue = replaced;
      }
    });
  }

  // Initial hydration on DOMContentLoaded (or immediately if already loaded)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { hydrate(); });
  } else {
    hydrate();
  }

  // Re-hydrate after Shopify section reloads in Theme Editor preview
  document.addEventListener('shopify:section:load', function (e) {
    hydrate(e.target);
  });

  // Re-hydrate after cart drawer / AJAX updates that may inject new markup
  document.addEventListener('eurus:cart:updated', function () { hydrate(); });
  document.addEventListener('cart:updated', function () { hydrate(); });

  // Re-hydrate after ANY DOM mutation that injects new content.
  // Covers variant-change (eurus:product-page-variant-select:updated:*), price-block
  // re-renders, cart-drawer AJAX swaps, and any future re-render path we don't yet
  // know about. We only walk the subtree that was actually added, and we de-dupe
  // bursts onto a single animation frame so this stays cheap.
  //
  // Our own replacements mutate characterData (nodeValue), not childList, so they
  // do not feed back into this observer.
  if (typeof MutationObserver === 'function' && document.body) {
    var pendingScopes = new Set();
    var rehydrateScheduled = false;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1) {
            pendingScopes.add(node);
          } else if (node.nodeType === 3 && node.parentNode) {
            pendingScopes.add(node.parentNode);
          }
        }
      }
      if (!pendingScopes.size || rehydrateScheduled) return;
      rehydrateScheduled = true;
      requestAnimationFrame(function () {
        rehydrateScheduled = false;
        var scopes = Array.from(pendingScopes);
        pendingScopes.clear();
        scopes.forEach(function (scope) {
          if (scope && scope.isConnected !== false) hydrate(scope);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
